import { Box, Grid, Paper, Skeleton, Tab, Tabs, Typography } from '@mui/material';
import dynamic from 'next/dynamic';
import { styled, useTheme } from '@mui/material/styles';
import React, { Suspense, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { marked } from 'marked';
import sanitize from '../../../../utils/sanitize';
import PropTypes from 'prop-types';
import useSWR from 'swr';
import _getTickerTextData from '../../../../../pages/api/_getTickerTextData';

// --- Dynamic Imports ---
const Lifestory = dynamic(() => import('./Lifestory'));
const RSIChart = dynamic(() => import('./RSIChart'));
const MetricsCharts = dynamic(() => import('./MetricsCharts'));
const PriceAction = dynamic(() => import('./PriceAction'));
const PriceTargets = dynamic(() => import('./PriceTargets'));
const SummaryRanks = dynamic(() => import('./SummaryRanks'));
const ValuationMetrics = dynamic(() => import('./ValuationMetrics'));
const AllNewsSummary = dynamic(() => import('./AllNewsSummary'));
const PricingAdBanner = dynamic(() => import('../../../PricingAdBanner'), {
  loading: () => <Grid size={{ md: 12, xs: 12 }}><Skeleton sx={{ height: 200, marginBottom: '8px' }} animation="wave" variant="rounded" /></Grid>, ssr: true, suspense: true
});
const StockAlerts = dynamic(() => import('./Alerts'));
const RevenuesBreakup = dynamic(() => import('./RevenuesBreakup'));
const Fair_Value = dynamic(() => import('./FairValue'));
const HistoricalCharts = dynamic(() => import('./HistoricalCharts'));
const Filings = dynamic(() => import('./Filings'), {
  loading: () => <Grid size={{ md: 12, xs: 12 }}><Skeleton sx={{ height: 470, marginBottom: '8px' }} animation="wave" variant="rounded" /></Grid>, ssr: true, suspense: true
});
const Filings_13G = dynamic(() => import('./Filings_13G'), {
  loading: () => <Grid size={{ md: 12, xs: 12 }}><Skeleton sx={{ height: 500, marginBottom: '8px' }} animation="wave" variant="rounded" /></Grid>, ssr: true, suspense: true
});
const AboutCompany = dynamic(() => import('./Company'));
const Financials = dynamic(() => import('./Financials'), {
  loading: () => <Grid size={{ md: 12, xs: 12 }}><Skeleton sx={{ height: 500, marginBottom: '8px' }} animation="wave" variant="rounded" /></Grid>, ssr: true, suspense: true
});
const FundHoldings = dynamic(() => import('./FundHoldings'), {
  loading: () => <Grid size={{ md: 12, xs: 12 }}><Skeleton sx={{ height: 460, marginBottom: '8px' }} animation="wave" variant="rounded" /></Grid>, ssr: true, suspense: true
});
const InsiderTransactions = dynamic(() => import('./InsiderTransactions'), {
  loading: () => <Grid size={{ md: 12, xs: 12 }}><Skeleton sx={{ height: 470, marginBottom: '8px' }} animation="wave" variant="rounded" /></Grid>, ssr: true, suspense: true
});
const FinancialsStatement = dynamic(() => import('./FinancialsStatement'), {
  loading: () => <Grid size={{ md: 12, xs: 12 }}><Skeleton sx={{ height: 500, marginBottom: '8px' }} animation="wave" variant="rounded" /></Grid>, ssr: true, suspense: true
});
const StockFAQs = dynamic(() => import('./StockFAQs'));

// --- Styled Components & Helpers ---
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`fin-tabpanel-${index}`}
      aria-labelledby={`fin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 0, minHeight: 177 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function finProps(index) {
  return {
    id: `fin-tab-${index}`,
    'aria-controls': `fin-tabpanel-${index}`,
  };
}

const FinTabs = styled(Tabs)({
  backgroundColor: '#fbfbfc',
  '& .MuiTabs-indicator': {
    backgroundColor: '#174c86',
  },
});

const FinTab = styled((props) => <Tab disableRipple {...props} />)(({ theme }) => ({
  textTransform: 'none',
  minWidth: 0,
  [theme.breakpoints.up('sm')]: {
    minWidth: 0,
  },
  fontWeight: theme.typography.fontWeightRegular,
  marginRight: theme.spacing(1),
  color: 'rgba(0, 0, 0, 0.95)',
  '&:hover': {
    color: '#174c86',
    opacity: 1,
  },
  '&.Mui-selected': {
    color: '#174c86',
    fontWeight: theme.typography.fontWeightMedium,
  },
  '&.Mui-focusVisible': {
    backgroundColor: '#174c861a',
  },
}));

const Item = styled(Paper)(({ theme }) => ({
  '& .MuiTableContainer-root::-webkit-scrollbar': {
    width: '8px',
    height: '5px',
  },
  '& .MuiTableContainer-root': {
    scrollbarWidth: 'thin',
  },
  '& .MuiTableContainer-root::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgb(176 176 177)',
    borderRadius: '6px',
    border: '1px solid rgba(224, 224, 224, 1)',
  },
  '& .MuiTableContainer-root::-webkit-scrollbar-track': {
    background: 'rgba(224, 224, 224, 1)',
  },
  backgroundColor: theme.palette.mode === 'dark' ? 'rgb(10, 25, 41)' : '#fff',
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  boxShadow: theme.palette.mode === 'dark' ? 0 : '0 0.5px 1px rgb(255 255 255 / 20%), 0 2px 3px rgb(255 255 255 / 10%)',
  border: theme.palette.mode === 'dark' ? '1px solid rgb(19, 47, 76)' : '1px solid #eae9ee',
  borderRadius: '7px',
  '& .Item-Head': {
    background: '#fbfbfc',
    marginBottom: 0,
    padding: theme.spacing(1, 2),
    borderRadius: '7px 7px 0 0',
    '& a': { textDecoration: 'none' },
    '& .option-title': {
      fontWeight: 600,
      color: '#13113c',
      fontSize: '.875rem',
    }
  },
  '& .table-note': {
    padding: '4px 6px',
    fontSize: '.875rem',
    color: theme.palette.text.very_light_desc,
    fontStyle: 'italic',
    textAlign: 'left',
    background: '#fff',
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
  }
}));

// --- Pure Utility Functions ---
const getFirstGroup = (data) => {
  if (!data || !Array.isArray(data)) return null;
  const groupedData = data.reduce((result, item) => {
    (result[item.group] = result[item.group] || []).push(item);
    return result;
  }, {});

  const sortOrder = ["PROD", "BUS", "GEO", "TOTAL"];
  const [firstGroup] = sortOrder
    .map(group => (groupedData[group] || [])[0])
    .filter(Boolean);

  return firstGroup;
};

const safeJSONParse = (str, fallback = null) => {
  if (!str) return fallback;
  if (typeof str !== 'string') return str;
  try {
    return JSON.parse(str);
  } catch (e) {
    console.error("JSON Parse error:", e);
    return fallback;
  }
};

// --- Sub-components ---
const FinancialReport = ({ info, tabData, isPremium, openDialog }) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);

  const handleChange = (event, newValue) => setTabValue(newValue);

  const { data } = useSWR(
    info?.ticker ? ["api/_getTickerTextData", info.ticker, ""] : null,
    _getTickerTextData,
    {
      keepPreviousData: true,
      refreshInterval: 12 * 3600000,
      revalidateOnFocus: false,
      onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
        if (error?.status === 404 || retryCount >= 10) return;
        setTimeout(() => revalidate({ retryCount }), 5000);
      },
    }
  );

  const { getTickerTextData } = data || {};
  const aiFinDescRaw = getTickerTextData?.find(d => d.halfCode === `TXT_FIN`)?.value || null;
  const aiFinDescHtml = useMemo(
    () => (aiFinDescRaw ? sanitize(marked(aiFinDescRaw)) : null),
    [aiFinDescRaw]
  );

  return (
    <Item className="s-card fin-stmt">
      <Box className="FINANCIALS_STATEMENT">
        <Box className="Item-Head" sx={{ display: 'flex', alignItems: 'center', mb: 0.5, px: 0.5 }}>
          <Typography component="h3" className="option-title" sx={{ mr: 2 }}>
            {`${info?.companyName || 'Company'}'s Consolidated Financial Statements`}:
          </Typography>
          {aiFinDescHtml && (
            <Typography itemProp="description" variant="body2" sx={{ color: '#333', fontSize: '.875rem', '& p': { my: .7 } }} className="font-weight-0 lh-base ml-2" dangerouslySetInnerHTML={{ __html: aiFinDescHtml }} />
          )}
        </Box>
        <Box component="div" className="Item-Body">
          <Box component="div" className="financials-tbl">
            <Box component="div" className="s-fin-body">
              <FinTabs value={tabValue} onChange={handleChange} indicatorColor="secondary" textColor="inherit" aria-label="financials statements">
                <FinTab label="Income Statement" {...finProps(0)} />
                <FinTab label="Balance Sheet" {...finProps(1)} />
                <FinTab label="Cashflow" {...finProps(2)} />
              </FinTabs>
              <Box>
                <TabPanel value={tabValue} index={0} dir={theme.direction}>
                  <FinancialsStatement tabData={tabData} sectionCode="income_statment" subTabCode="IS" info={info} isPremium={isPremium} openDialog={openDialog} />
                </TabPanel>
                <TabPanel value={tabValue} index={1} dir={theme.direction}>
                  <FinancialsStatement tabData={tabData} sectionCode="balance_sheet" subTabCode="BS" info={info} isPremium={isPremium} openDialog={openDialog} />
                </TabPanel>
                <TabPanel value={tabValue} index={2} dir={theme.direction}>
                  <FinancialsStatement tabData={tabData} sectionCode="cashflow_statement" subTabCode="CF" info={info} isPremium={isPremium} openDialog={openDialog} />
                </TabPanel>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Item>
  );
};

FinancialReport.propTypes = {
  info: PropTypes.object,
  tabData: PropTypes.array,
  isPremium: PropTypes.bool,
  openDialog: PropTypes.func,
};

const LoadingFallback = () => (
  <Box role="status" aria-live="polite" sx={{ p: 1 }}>
    <Skeleton animation="wave" variant="rounded" sx={{ height: 120 }} />
    <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
      Loading…
    </span>
  </Box>
);

const SectionHeader = ({ title, description, titleComponent = 'h6', mb = 3 }) => (
  <Grid
    size={{ md: 12, xs: 12 }}
    sx={{ '& .MuiDivider-root::before, & .MuiDivider-root::after': { borderTop: 'thin solid #000' } }}
  >
    <Typography
      component={titleComponent}
      variant="h6"
      itemProp="name"
      sx={{ fontWeight: 700, color: '#174c86', textAlign: 'center', fontSize: '1.5rem', pt: 2, pb: 1 }}
    >
      {title}
    </Typography>
    <Typography
      component="p"
      variant="body1"
      itemProp="description"
      sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: '800px', mx: 'auto', mb, fontSize: '1rem', lineHeight: 1.6 }}
    >
      {description}
    </Typography>
  </Grid>
);

SectionHeader.propTypes = {
  title: PropTypes.node.isRequired,
  description: PropTypes.node.isRequired,
  titleComponent: PropTypes.string,
  mb: PropTypes.number,
};

export default function Summary({
  tabData, info, tabCode, data,
  realtimeData, getTradingHours,
  themes, rsi_data, stockAlerts,
  isPremium, token, openDialog, revenues_data, stock_faqs, bucket,
  chart_events, chart_periods, chart_templates, summary_ranks,
  // heatmapSettings, heatmapData,
  sector, industry, desk, insider_data, fund_data,
  filings_data, watchlist_metrics,
}) {
  const theme = useTheme();
  const { ticker } = info || {};
  const { stock_data } = data || {};
  const [visible, setVisible] = useState(false);
  const revealSentinelRef = useRef(null);

  const firstGroup = useMemo(() => getFirstGroup(revenues_data), [revenues_data]);

  const summaryColors = useMemo(() => {
    const entry = tabData?.find(d => d.section_code === "summary_ranks");
    return safeJSONParse(entry?.settings);
  }, [tabData]);

  const metricsChartsData = useMemo(
    () => tabData?.find(d => d.section_code === "revenue_break"),
    [tabData]
  );

  useEffect(() => {
    if (visible) return undefined;

    const node = revealSentinelRef.current;

    if (typeof IntersectionObserver === 'undefined' || !node) {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin: '200px 0px', threshold: 0 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  const getDerivedValue = useCallback((val, formula) => {
    if (typeof formula !== 'string') return val;
    const { realtime_stock_data } = realtimeData || {};

    let exp = formula.replace(/#value/g, val);
    const price = realtime_stock_data?.[0]?.price;
    if (price != null) {
      exp = exp.replace(/#price/g, price);
    } else if (exp.includes('#price')) {
      return val;
    }
    try {
      // eslint-disable-next-line no-eval
      return eval(exp);
    } catch (err) {
      console.error("Evaluation Error:", err);
      return val;
    }
  }, [realtimeData]);

  return (
    <Box sx={{ width: '100%', mb: 1, position: 'relative' }}>
      <Grid container spacing={1.2} justifyContent="flex-start" alignItems="flex-start">
        <Grid size={{ md: 12, xs: 12 }}>
          <Grid container rowSpacing={0.5} columnSpacing={0.5} direction={{ sm: "row-reverse" }}>
            <Grid size={{ md: 7, xs: 12 }}>
              <Grid container rowSpacing={0.5} columnSpacing={0.5}>
                <PriceAction
                  secHeading="Price Action"
                  data={data}
                  tabData={tabData}
                  ticker={ticker}
                  info={info}
                  themes={themes}
                  flex={false}
                />
                <Lifestory
                  tabData={tabData}
                  ticker={ticker}
                  info={info}
                  stock_data={stock_data}
                  themes={themes}
                  getDerivedValue={getDerivedValue}
                  data={data}
                  tabCode={tabCode}
                  realtimeData={realtimeData}
                  getTradingHours={getTradingHours}
                  desk={desk}
                  insider_data={insider_data}
                  fund_data={fund_data}
                  filings_data={filings_data}
                />
                <RSIChart
                  tabData={tabData}
                  rsi_data={rsi_data}
                  themes={themes}
                  ticker={ticker}
                  info={info}
                  desk={desk}
                />
              </Grid>
            </Grid>
            <Grid size={{ md: 5, xs: 12 }}>
              <Grid container columnSpacing={0.5}>
                <Grid size={{ md: 6, xs: 12 }} sx={{ display: { xs: 'none', md: 'block' } }}>
                  <Grid container rowSpacing={0.5} columnSpacing={0.5} sx={{ height: '100%' }}>
                    <ValuationMetrics
                      ticker={ticker}
                      realtimeData={info}
                      isPremium={isPremium}
                      token={token}
                    />
                    <MetricsCharts
                      secHeading="Valuation"
                      tabData={tabData}
                      ticker={ticker}
                      info={info}
                      getDerivedValue={getDerivedValue}
                      themes={themes}
                      columnWidth={12}
                      noOfChartsNeeded={1}
                      desk={desk}
                    />
                    <SummaryRanks
                      secHeading="Summary Ranks"
                      ticker={ticker}
                      info={info}
                      summary_ranks={summary_ranks}
                      summary_colors={summaryColors}
                      columnWidth={12}
                    />
                  </Grid>
                </Grid>
                <Grid size={{ md: 6, xs: 12 }}>
                  <Box component="div" sx={{ display: { xs: 'none', md: 'block' }, width: '100%' }}>
                    <PriceTargets watchlist_metrics={watchlist_metrics} ticker={ticker} isPremium={isPremium} token={token} />
                  </Box>
                  <MetricsCharts
                    secHeading="Fundamentals"
                    tabData={tabData}
                    ticker={ticker}
                    info={info}
                    getDerivedValue={getDerivedValue}
                    themes={themes}
                    columnWidth={12}
                    noOfChartsNeeded={2}
                    desk={desk}
                  />
                </Grid>
                <Grid size={{ md: 6, xs: 12 }}>
                  <MetricsCharts
                    secHeading="Profitability"
                    tabData={tabData}
                    ticker={ticker}
                    info={info}
                    getDerivedValue={getDerivedValue}
                    themes={themes}
                    columnWidth={12}
                    noOfChartsNeeded={0}
                    desk={desk}
                  />
                </Grid>
                <Grid size={{ md: 6, xs: 12 }}>
                  <MetricsCharts
                    secHeading="Investor Care"
                    tabData={tabData}
                    ticker={ticker}
                    info={info}
                    getDerivedValue={getDerivedValue}
                    themes={themes}
                    columnWidth={12}
                    noOfChartsNeeded={0}
                    desk={desk}
                  />
                </Grid>
                {stockAlerts?.length > 0 && (
                  <Grid size={{ md: 12, xs: 12 }} sx={{ display: { xs: 'block', md: 'none', sm: 'block' } }}>
                    <StockAlerts tabData={tabData} stockAlerts={stockAlerts} info={info} />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <AllNewsSummary info={info} />

        {revenues_data && (
          <SectionHeader
            title="Revenue & Growth"
            description={`How ${info?.companyName || 'Company'} generates revenue and covers its expenses, based on the latest reported earnings.`}
          />
        )}

        <Grid size={{ md: 3.5, xs: 12 }}>
          <Grid container rowSpacing={1.2} columnSpacing={0.5} sx={{ height: '100%' }}>
            <RevenuesBreakup
              secHeading="Fundamentals"
              tabData={tabData}
              revenues_data={revenues_data}
              ticker={ticker}
              info={info}
              getDerivedValue={getDerivedValue}
              themes={themes}
              columnWidth={4}
              noOfChartsNeeded={2}
              openDialog={openDialog}
              isPremium={isPremium}
              desk={desk}
            />
          </Grid>
        </Grid>

        <Grid size={{ md: metricsChartsData && firstGroup ? 8.5 : 12, xs: 12 }}>
          <Grid container rowSpacing={1.2} columnSpacing={0.5} sx={{ height: '100%' }}>
            <HistoricalCharts
              secHeading="Fundamentals"
              tabData={tabData}
              ticker={ticker}
              info={info}
              getDerivedValue={getDerivedValue}
              themes={themes}
              columnWidth={4}
              noOfChartsNeeded={2}
              openDialog={openDialog}
              isPremium={isPremium}
              fullWidth={!(metricsChartsData && firstGroup)}
              events={chart_events}
              periods={chart_periods}
              templates={chart_templates}
              desk={desk}
            />
          </Grid>
        </Grid>

        {!visible && (
          <Grid
            ref={revealSentinelRef}
            size={{ md: 12, xs: 12 }}
            aria-hidden="true"
            sx={{ height: 1, p: 0, m: 0 }}
          />
        )}

        {visible && (
          <>
            {isPremium ?
              <SectionHeader
                title="Valuation"
                description={`Explore how the fair value estimates of ${info?.companyName || 'the company'} have evolved, and how different market scenarios may impact future returns.`}
              /> : null}
            <Suspense fallback={<LoadingFallback />}>
              <Fair_Value info={info} tabData={tabData} themes={themes} tabCode={'SUM'} isPremium={isPremium} token={token} openDialog={openDialog} bucket={bucket} desk={desk} />
            </Suspense>

            <SectionHeader
              title="Insider Trading"
              description={`See whether executives are buying or selling shares of ${info?.companyName || 'the company'} through the latest Form 4 filings and the Grufity Insider Selling Index.`}
            />
            <Suspense fallback={<LoadingFallback />}>
              <InsiderTransactions tabData={tabData} ticker={ticker} isPremium={isPremium} openDialog={openDialog} company={info?.companyName} themes={themes} desk={desk} />
            </Suspense>

            <SectionHeader
              title="Funds Popularity"
              mb={4}
              description="Monitor how major fund managers are buying, selling, or adjusting their portfolio stakes, alongside active options volume."
            />
            <Suspense fallback={<LoadingFallback />}>
              <FundHoldings tabData={tabData} info={info} company={info?.companyName} isPremium={isPremium} openDialog={openDialog} themes={themes} desk={desk} />
            </Suspense>

            <SectionHeader
              title="Financials"
              mb={4}
              description={`See exactly how much money ${info?.companyName || 'the company'} make, what they own versus what they owe, and how revenue turns into net profit.`}
            />
            <Suspense fallback={<LoadingFallback />}>
              <Financials theme={theme} tabData={tabData} ticker={ticker} info={info} isPremium={isPremium} openDialog={openDialog} />
            </Suspense>

            <PricingAdBanner
              bannerType={`${ticker}-stock-inapp-ad`}
              adTitle={"Get Full Access to Grufity – and Unlimited Downloads"}
              adDesc={"Gain full access to fair value, watchlists, stock screener, unlimited page visits, and additional features by joining as a member today."}
            />

            <Grid size={{ md: 12, xs: 12 }}>
              <Suspense fallback={<LoadingFallback />}>
                <FinancialReport tabData={tabData} info={info} isPremium={isPremium} openDialog={openDialog} />
              </Suspense>
            </Grid>

            <SectionHeader
              title="News & Updates"
              mb={4}
              description="Monitor recent SEC filings to track major corporate announcements, and see which institutional shareholders hold the largest stakes."
            />
            <Suspense fallback={<LoadingFallback />}>
              <Filings_13G info={info} limit={15} isPremium={isPremium} openDialog={openDialog} />
            </Suspense>
            <Suspense fallback={<LoadingFallback />}>
              <Filings info={info} limit={12} isPremium={isPremium} openDialog={openDialog} />
            </Suspense>
          </>
        )}

        {info?.companyName && (
          <Grid size={{ md: 12, xs: 12 }}>
            <AboutCompany info={info} sector={sector} industry={industry} />
          </Grid>
        )}

        <Grid size={{ md: 12, xs: 12 }}>
          <StockFAQs info={info} stock_faqs={stock_faqs} />
        </Grid>
      </Grid>
    </Box>
  );
}
